import { KeyboardEvent, useEffect, useState } from 'react';

import {
  Box,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  Fade,
  InputAdornment,
  InputBase,
  List,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
  WithStyles,
  createStyles,
  styled,
  withStyles,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

import { useDialog, useNavigate } from '@spotify/confidence-core-react';
import { SearchDocument, useSearch } from '@spotify/confidence-search-react';

import { ResultListItem } from './ResultListItem';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: 'calc(100vh - 400px)',
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      flexShrink: 0,
    },
    keyboard: {
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: theme.shape.borderRadius / 2,
      padding: theme.spacing(0, 0.5),
      textTransform: 'uppercase',
    },
    keyboardContainer: {
      padding: 'var(--bui-space-3) 0 0',
    },
    results: {
      flex: '1 1 auto',
      height: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    input: {
      paddingLeft: 0,
      paddingTop: 0,
    },
  });

const ResultContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(0, -1),
}));

export interface AppSearchDialogProps extends WithStyles<typeof styles> {}

const AppSearchDialog = (props: AppSearchDialogProps) => {
  const { classes } = props;

  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { closeDialog } = useDialog();

  const { results, loading } = useSearch(query, []);

  const handleClose = () => closeDialog();

  const [selected, setSelected] = useState(0);

  // Cache the results to prevent flickering
  const [cachedResults, setCachedResults] = useState<SearchDocument[]>([]);

  // Update the cache when the results change and we are no longer
  // loading.

  // Since arrays are reference types, we need to compare the stringified
  // version of the results to determine if we need to update the cache
  const resultKey = JSON.stringify(results.map(r => r.id));

  useEffect(
    () => {
      if (!loading) setCachedResults(results);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resultKey, loading],
  );

  // Update the selected index when the results change
  const cachedResultsKey = JSON.stringify(cachedResults.map(r => r.id));

  useEffect(
    () => {
      setSelected(0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cachedResultsKey],
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!cachedResults.length || !cachedResults[selected]) {
      return;
    }

    if (e.key === 'Enter') {
      navigate(cachedResults[selected].href);
      handleClose();
    } else if (e.key === 'ArrowDown') {
      setSelected(curr => Math.min(curr + 1, cachedResults.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setSelected(curr => Math.max(curr - 1, 0));
      e.preventDefault();
    }
  };

  return (
    <DialogContent className={classes.root}>
      <div className={classes.header}>
        <InputBase
          placeholder="What are you looking for?"
          inputProps={{ autoFocus: true }}
          classes={{
            input: classes.input,
          }}
          endAdornment={
            <InputAdornment position="end">
              <Fade in={loading}>
                <CircularProgress size={20} color="inherit" />
              </Fade>
            </InputAdornment>
          }
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          value={query}
          fullWidth
          margin="none"
        />

        <StyledDivider orientation="horizontal" />

        <Fade in={cachedResults.length > 0}>
          <Box
            display="flex"
            justifyContent="space-between"
            className={classes.keyboardContainer}
          >
            <Box display="flex" alignItems="center" gridGap={8}>
              <ArrowUpwardIcon className={classes.keyboard} />
              <ArrowDownwardIcon className={classes.keyboard} />
              <Typography variant="body2" color="textSecondary">
                Navigate
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gridGap={8}>
              <Typography className={classes.keyboard} variant="body2">
                Enter
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Open
              </Typography>
            </Box>
          </Box>
        </Fade>
      </div>

      <ResultContainer className={classes.results}>
        {cachedResults.length === 0 && query && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body2" color="textSecondary">
              No results found for "{query}"
            </Typography>
          </Box>
        )}

        <List>
          {cachedResults.map((result, i) => (
            <ResultListItem
              key={`${result.id}:${i}`}
              selected={i === selected}
              href={result.href}
              rank={i}
            >
              <ListItemIcon>{result.icon}</ListItemIcon>
              <ListItemText
                primary={result.title}
                secondary={result.subtitle}
              />
              <Box marginLeft="auto">
                <Chip label={result.category} size="small" />
              </Box>
            </ResultListItem>
          ))}
        </List>
      </ResultContainer>
    </DialogContent>
  );
};

export default withStyles(styles)(AppSearchDialog);
